/**
 * Whether this visitor can use the app, where Cloudflare blocks the API.
 *
 * The blocked countries are the Cloudflare rule's own, never a list here (the user, 2026-10-19: "always match the
 * cloudflare rule automatically ... so i can unblock or block more on cloudflare right away"). Once a session the page
 * asks the API's /public/region with the device's time zone:
 *  - the API answers: blocked when that time zone belongs to a country the rule blocks, which is a VPN from there
 *    (the API refuses such a signed-in person too, auth/clerk_session.py);
 *  - Cloudflare refuses the request (its block page has no CORS header, so fetch fails) while Cloudflare itself is
 *    reachable (/cdn-cgi/trace on the site's domain answers) AND the API is up: the visitor's own IP is in a blocked
 *    country. The API being down fails the browser's request the same way (Cloudflare's 521 has no CORS header
 *    either), so the site's own API proxy (functions/api/latest/email, which reaches the API from Cloudflare, not
 *    from the visitor) is asked too: an answer under 500 means the API is up; a 5xx or no answer is an outage, and an
 *    outage is never a country (2026-10-24 06:48-09:26 CEST: nginx was down and every visitor saw "not available in
 *    your country"; the user: "it shouldn't show not available in your country in such cases").
 * Everything else fails open: a slow API, an older API with no /public/region, no network at all.
 *
 * Clerk's sign-up runs on Clerk's own servers, outside that rule, so without this a visitor there could make an
 * account with nothing behind it (1,215 such accounts in two days, 2026-10-14..16).
 */
import { useEffect, useState } from 'react';
import { config } from '../config';

const KEY = 'app.region.state';
const WHY = 'app.region.why';
const LOOKUP_MS = 2500;

/** The `loc=` line of a /cdn-cgi/trace answer, or null. */
export function countryFromTrace(text) {
  const m = /^loc=([A-Z]{2})$/m.exec(String(text || ''));
  return m ? m[1] : null;
}

export function deviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

const TIMED_OUT = Symbol('timed out');
const inTime = (promise) => Promise.race([
  promise,
  new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), LOOKUP_MS)),
]);

/** Cloudflare answers on the site's own domain: the network is up. */
function cloudflareReachable(fetchImpl) {
  return inTime(
    fetchImpl('/cdn-cgi/trace', { cache: 'no-store' })
      .then((res) => (res.ok ? res.text() : ''))
      .then((text) => countryFromTrace(text) !== null)
      .catch(() => false),
  ).then((answer) => answer === true);
}

/** The API answers through the site's own proxy, which reaches it from Cloudflare (no country rule, no CORS). */
function apiUpViaSite(fetchImpl) {
  return inTime(
    fetchImpl('/api/latest/email/preferences', { cache: 'no-store' })
      .then((res) => typeof res?.status === 'number' && res.status > 0 && res.status < 500)
      .catch(() => false),
  ).then((answer) => answer === true);
}

/** 'blocked' | 'open', asked of the API. */
export async function askRegion(fetchImpl, base = config.API_URL, zone = deviceTimeZone()) {
  return (await askRegionWhy(fetchImpl, base, zone)).state;
}

/**
 * {state, why}: why is 'zone' when the API said the device's time zone is in a blocked country, 'refused' when the
 * API could not be reached while Cloudflare and the API itself (through the site's proxy) could (the visitor's IP is
 * in one), else null.
 */
// A refused request is asked once more this long after: one dropped request (a Wi-Fi or VPN blip) made the owner's
// own browser in Germany read "not available in your country" for the whole visit (2026-10-26); a country the rule
// blocks refuses both
export const RETRY_MS = 1000;
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function askRegionWhy(fetchImpl, base = config.API_URL, zone = deviceTimeZone(), wait = pause) {
  if (!base) return { state: 'open', why: null };
  const url = `${base.replace(/\/$/, '')}/public/region?tz=${encodeURIComponent(zone || '')}`;
  const ask = () => inTime(
    fetchImpl(url, { cache: 'no-store' })
      .then(async (res) => (res.ok ? ((await res.json())?.blocked ? 'blocked' : 'open') : 'open'))
      .catch(() => 'refused'),
  );
  let answer = await ask();
  if (answer === 'refused') {
    await wait(RETRY_MS);
    answer = await ask();
  }
  if (answer === 'refused') {
    const [network, apiUp] = await Promise.all([cloudflareReachable(fetchImpl), apiUpViaSite(fetchImpl)]);
    return network && apiUp ? { state: 'blocked', why: 'refused' } : { state: 'open', why: null };
  }
  return answer === 'blocked' ? { state: 'blocked', why: 'zone' } : { state: 'open', why: null };
}

/** Why this visit was found blocked ('zone' | 'refused'), or null. */
export function regionWhy(storage) {
  try {
    const store = storage === undefined ? window.sessionStorage : storage;
    return store?.getItem(WHY) || null;
  } catch {
    return null;
  }
}

/** 'blocked' | 'open', decided once a session. */
export async function regionState({
  fetchImpl = typeof fetch !== 'undefined' ? fetch : null,
  storage,
  base,
  zone,
  wait,
} = {}) {
  let store = storage;
  try {
    if (store === undefined) store = window.sessionStorage;
  } catch {
    store = null;
  }
  try {
    const kept = store?.getItem(KEY);
    if (kept === 'blocked' || kept === 'open') return kept;
  } catch {
    // storage refused: ask
  }
  if (!fetchImpl) return 'open';
  const { state, why } = await askRegionWhy(fetchImpl, base === undefined ? config.API_URL : base, zone === undefined ? deviceTimeZone() : zone, wait);
  try {
    store?.setItem(KEY, state);
    if (why) store?.setItem(WHY, why);
  } catch {
    // nothing kept: the next page asks again
  }
  return state;
}

let pending = null;

/** 'pending' | 'blocked' | 'open' */
export function useRegion() {
  const [state, setState] = useState('pending');
  useEffect(() => {
    let live = true;
    pending = pending || regionState().finally(() => { pending = null; });
    pending.then((s) => { if (live) setState(s); });
    return () => {
      live = false;
    };
  }, []);
  return state;
}
